import AutorenewIcon from '@mui/icons-material/Autorenew'
import {
	Autocomplete,
	Box,
	Divider,
	FormControl,
	Grid2,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Loader } from '@/components'
import { Form, useGetNamingConfig } from '@/components/UberForm'
import { useCanGenerateNaming } from '@/components/UberForm/Input/Naming/useCanGenerateNaming'
import { CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX } from '@/constants'
import {
	getAllBusinessDomains,
	getStereotypes,
	getSystemUsers,
	getUsersForCombo,
} from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import { ObjectSettingsUpdateDto, StructureObjectDto } from '@/endpoints/models'
import { PageType } from '@/enums'
import { useAppContext, useAppDispatch, useAppStore, useCustomAttributesInitValues } from '@/hooks'
import { CustomAttributes } from '@/pages/User/pages/Home/components/CustomAttributes'
import { ObjectSettings, Status } from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	callNamingWorkingData,
	CallNamingWorkingDataParams,
} from '@/store/modules/table/utils/callNaming'
import { NamingDtoTypeEnum, SqlTypeEnum } from '@/types'
import { formatCodeByCase } from '@/utils'
import { logError } from '@/utils/errorHandling'

import { DescriptionFieldWithFullscreen } from '../../components/DescriptionFieldWithFullscreen'
// Backup: ObjectSettingsControlV2 (chips + add-one-by-one)
import { ObjectSettingsMultiselectV2 } from '../../components/ObjectSettingsMultiselectV2'
import {
	useViewFieldBoxHighlightSx,
	useViewFieldHighlightSx,
} from '../../components/useViewFieldHighlightSx'
import { useViewDataContext, ViewFormData } from '../../ViewDataContext'

// Form values type
interface OverviewFormValues {
	businessDomainId: number | null
	code: string
	comment: string
	description: string
	name: string
	objectSettings: ObjectSettings[]
	ownerId: number | null
	responsibleAnalystId: number | null
	responsiblePersonId: number | null
	status: Status | ''
	stereotypeId: number | null
}

// Input font comes from theme (MuiInputBase.input); this only adjusts select/autocomplete input if needed
const inputFontSizeSx = {}

// MDD-2595: Name and Code as primary identification — semi-bold
const nameCodeHighlightSx = {
	'& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 600 },
}

// Tighter section header for smaller gaps between sections
const TightSectionHeader = ({ title }: { title: string }) => (
	<Box sx={{ mt: 1.5, mb: 0.5 }}>
		<Typography
			variant="overline"
			sx={{
				fontWeight: 600,
				color: 'text.secondary',
				letterSpacing: 1,
				fontSize: '1rem',
			}}
		>
			{title}
		</Typography>
		<Divider sx={{ mt: 0.5 }} />
	</Box>
)

export const OverviewV2 = () => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const {
		node,
		form,
		editMode,
		systemNodeId,
		onChange,
		isLoading,
		serverDataRevision,
	} = useViewDataContext()

	const [users, isUserLoading] = useApi(getUsersForCombo())
	const [systemUsers, isSystemUserLoading] = useApi(
		getSystemUsers(systemNodeId),
	)
	const [stereotypes] = useApi(
		getStereotypes({ type: StructureObjectDto.TypeEnum.VIEW }),
	)
	const [businessDomains] = useApi(getAllBusinessDomains())

	// Code generation
	const { generateElementCode } = useCanGenerateNaming(node)
	// ViewFormData compatible with naming config shape expected by useGetNamingConfig
	const namingConfig = useGetNamingConfig(form as any, node)
	const [isGeneratingCode, setIsGeneratingCode] = useState(false)

	// Get isUpperCase directly from system using systemNodeId from context
	// (useCanGenerateNaming may fail to find systemId if node doesn't have proper parent chain)
	const systemsSlice = useAppStore((state) => state.system.systems)
	const letterCaseToggle = systemsSlice[systemNodeId]?.form?.letterCaseToggle
	const isUpperCase =
		letterCaseToggle === 'true' || letterCaseToggle === undefined

	// Custom attributes: definitions from hook (combo), values from overview
	const {
		parseCustomAttribute,
		customAttributes: customAttributeDefinitions,
	} = useCustomAttributesInitValues(
		form ?? {},
		StructureObjectDto.TypeEnum.VIEW,
	)

	// Merge definitions with overview values so we show all VIEW attributes (with value from overview or empty)
	const customAttributesFromOverview = useMemo(() => {
		const overviewValues = form?.customAttributes ?? []
		return (customAttributeDefinitions ?? []).map((def) => {
			const existing = overviewValues.find(
				(ca: { id?: string | number }) => String(ca.id) === String(def.id),
			)
			return {
				...def,
				value: existing != null ? (existing as { value?: unknown }).value : undefined,
			}
		})
	}, [customAttributeDefinitions, form?.customAttributes])

	const nameHighlightSx = useViewFieldHighlightSx('name', form?.name)
	const codeHighlightSx = useViewFieldHighlightSx('code', form?.code)
	const stereotypeHighlightSx = useViewFieldHighlightSx(
		'stereotypeId',
		(form as ViewFormData | undefined)?.stereotypeId,
	)
	const statusHighlightSx = useViewFieldHighlightSx('status', form?.status)
	const responsiblePersonHighlightSx = useViewFieldHighlightSx(
		'responsiblePersonId',
		form?.responsiblePersonId,
	)
	const responsibleAnalystHighlightSx = useViewFieldHighlightSx(
		'responsibleAnalystId',
		form?.responsibleAnalystId,
	)
	const ownerHighlightSx = useViewFieldHighlightSx('ownerId', form?.ownerId)
	const objectSettingsHighlightSx = useViewFieldHighlightSx(
		'objectSettings',
		form?.objectSettings,
	)
	const descriptionHighlightSx = useViewFieldBoxHighlightSx(
		'description',
		form?.description,
	)
	const commentHighlightSx = useViewFieldBoxHighlightSx('comment', form?.comment)

	const overviewInitialValues = useMemo(
		() =>
			customAttributesFromOverview.reduce(
				(acc: Record<string, unknown>, ca) => {
					const key = `${CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX}.${ca.id}.${ca.name}`
					acc[key] = ca.value
					return acc
				},
				{},
			),
		[customAttributesFromOverview],
	)

	const handleCustomAttributeChange = useCallback(
		(fieldName: string, value: unknown) => {
			onChange(
				parseCustomAttribute(
					{ [fieldName]: value },
					form?.customAttributes ?? [],
					customAttributeDefinitions ?? [],
				),
			)
		},
		[onChange, parseCustomAttribute, form?.customAttributes, customAttributeDefinitions],
	)

	// Initialize react-hook-form
	const { control, reset, setValue } = useForm<OverviewFormValues>({
		defaultValues: {
			name: form?.name ?? '',
			code: form?.code ?? '',
			description: form?.description ?? '',
			comment: form?.comment ?? '',
			status: form?.status ?? '',
			stereotypeId: (form as ViewFormData)?.stereotypeId ?? null,
			responsiblePersonId: form?.responsiblePersonId ?? null,
			responsibleAnalystId: form?.responsibleAnalystId ?? null,
			businessDomainId: (form as ViewFormData)?.businessDomainId ?? null,
			ownerId: form?.ownerId ?? null,
			objectSettings: form?.objectSettings ?? [],
		},
	})

	// Sync form values to parent onChange when they change
	const handleFieldChange = useCallback(
		(fieldName: keyof OverviewFormValues, value: unknown) => {
			if (!editMode) return

			const update: Record<string, unknown> = { [fieldName]: value }

			// Add related name fields for ID fields
			if (fieldName === 'stereotypeId') {
				update.stereotypeName = stereotypes?.find((s) => s.id === value)?.name
			}
			if (fieldName === 'responsiblePersonId') {
				update.responsiblePersonName = users?.find((u) => u.id === value)?.value
			}
			if (fieldName === 'responsibleAnalystId') {
				update.responsibleAnalystName = users?.find(
					(u) => u.id === value,
				)?.value
			}
			if (fieldName === 'businessDomainId') {
				update.businessDomainName = businessDomains?.find(
					(d) => d.id === value,
				)?.name
			}
			if (fieldName === 'ownerId') {
				update.ownerName = systemUsers?.find((u) => u.id === value)?.name
			}

			onChange(update)
		},
		[editMode, onChange, stereotypes, users, businessDomains, systemUsers],
	)

	// Handle code generation (manual trigger when auto-generate is off)
	const handleGenerateCode = useCallback(
		async (setFieldValue: (value: string) => void) => {
			if (!namingConfig || isGeneratingCode) return

			setIsGeneratingCode(true)
			try {
				const naming = await dispatch(
					callNamingWorkingData(namingConfig as CallNamingWorkingDataParams),
				)
				const result = naming?.result
				if (result) {
					const parsedResult = formatCodeByCase(result, isUpperCase)
					setFieldValue(parsedResult)
					handleFieldChange('code', parsedResult)
				}
			} catch (error) {
				logError(error, { operation: 'OverviewV2.generateCode' })
			} finally {
				setIsGeneratingCode(false)
			}
		},
		[namingConfig, isGeneratingCode, dispatch, isUpperCase, handleFieldChange],
	)

	// When "generate code from name" is on: update Code on Name blur using current name from field
	const handleNameBlur = useCallback(
		async (currentName: string) => {
			if (!generateElementCode || !node || isGeneratingCode) return
			setIsGeneratingCode(true)
			try {
				const formWithName = { ...form, name: currentName }
				const config: CallNamingWorkingDataParams = [
					NamingDtoTypeEnum.VIEW_CODE,
					node.id,
					SqlTypeEnum.VIEW,
					{ body: { data: JSON.stringify(formWithName) } },
				]
				const naming = await dispatch(callNamingWorkingData(config))
				const result = naming?.result
				if (result) {
					const parsed = formatCodeByCase(result, isUpperCase)
					setValue('code', parsed)
					handleFieldChange('code', parsed)
				}
			} catch (error) {
				logError(error, { operation: 'OverviewV2.generateCodeOnNameBlur' })
			} finally {
				setIsGeneratingCode(false)
			}
		},
		[
			generateElementCode,
			node,
			form,
			isGeneratingCode,
			dispatch,
			isUpperCase,
			setValue,
			handleFieldChange,
		],
	)

	// Track previous editMode to detect entering edit mode
	const prevEditModeRef = useRef(editMode)
	const hasInitializedRef = useRef(false)

	const resetFormFromContext = useCallback(() => {
		if (!form) return
		reset({
			name: form.name ?? '',
			code: form.code ?? '',
			description: form.description ?? '',
			comment: form.comment ?? '',
			status: form.status ?? '',
			stereotypeId: (form as ViewFormData).stereotypeId ?? null,
			responsiblePersonId: form.responsiblePersonId ?? null,
			responsibleAnalystId: form.responsibleAnalystId ?? null,
			businessDomainId: (form as ViewFormData).businessDomainId ?? null,
			ownerId: form.ownerId ?? null,
			objectSettings: form.objectSettings ?? [],
		})
	}, [form, reset])

	// Reset form when:
	// 1. Initial data loads
	// 2. Entering / exiting edit mode
	// 3. Server data revision bumps (e.g. AI updated the view in read mode)
	useEffect(() => {
		const enteringEditMode = !prevEditModeRef.current && editMode
		const exitingEditMode = prevEditModeRef.current && !editMode
		const initialLoad = !hasInitializedRef.current && form

		if ((initialLoad || enteringEditMode || exitingEditMode) && form) {
			resetFormFromContext()
			hasInitializedRef.current = true
		}

		prevEditModeRef.current = editMode
	}, [form, editMode, resetFormFromContext])

	useEffect(() => {
		if (!hasInitializedRef.current || serverDataRevision === 0) return
		resetFormFromContext()
	}, [serverDataRevision, resetFormFromContext])

	// Status options
	const viewStates = useMemo<Array<{ label: string; value: Status }>>(
		() => [
			{
				label: t('VIEW_STATUS_LOGICAL_IN_PROGRESS'),
				value: 'logical_in_progress',
			},
			{ label: t('VIEW_STATUS_LOGICAL_COMPLETED'), value: 'logical_completed' },
			{
				label: t('VIEW_STATUS_PHYSICAL_IN_PROGRESS'),
				value: 'physical_in_progress',
			},
			{
				label: t('VIEW_STATUS_PHYSICAL_COMPLETED'),
				value: 'physical_completed',
			},
			{ label: t('VIEW_STATUS_FROZEN'), value: 'frozen' },
		],
		[t],
	)

	const _getStatusLabel = useCallback(
		(value: string) =>
			viewStates.find((s) => s.value === value)?.label ?? value,
		[viewStates],
	)

	const getStereotypeLabel = useCallback(
		(id: number | null) => stereotypes?.find((s) => s.id === id)?.name ?? '',
		[stereotypes],
	)

	const getSelectedUser = useCallback(
		(id: number | null | undefined) => users?.find((u) => u.id === id) ?? null,
		[users],
	)

	const getSelectedSystemUser = useCallback(
		(id: number | null | undefined) =>
			systemUsers?.find((u) => u.id === id) ?? null,
		[systemUsers],
	)

	const handleObjectSettingsChange = useCallback(
		(newValue: ObjectSettings[]) => {
			onChange({ objectSettings: newValue })
		},
		[onChange],
	)

	if (isLoading || !form) {
		return <Loader loaded={false} />
	}

	const isReadOnly = !editMode

	return (
		<Stack
			component="div"
			sx={{
				flex: 1,
				overflow: 'auto',
				p: 2,
				px: 3,
				py: 2,
				gap: 2,
			}}
		>
			{/* Headerless: Identification + Objects */}
			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<Tooltip title={t('VIEW_NAME_TOOLTIP')} placement="top" arrow>
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									onChange={(e) => {
										field.onChange(e)
										handleFieldChange('name', e.target.value)
									}}
									onBlur={() => {
										field.onBlur()
										handleNameBlur(field.value)
									}}
									label={t('VIEW_NAME')}
									disabled={isReadOnly}
									size="small"
									fullWidth
									sx={[nameCodeHighlightSx, nameHighlightSx]}
								/>
							)}
						/>
					</Tooltip>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<Tooltip title={t('VIEW_CODE_TOOLTIP')} placement="top" arrow>
						<Controller
							name="code"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									onChange={(e) => {
										field.onChange(e)
										handleFieldChange('code', e.target.value)
									}}
									label={t('VIEW_CODE')}
									disabled={isReadOnly || generateElementCode}
									size="small"
									fullWidth
									sx={[nameCodeHighlightSx, codeHighlightSx]}
									InputProps={{
										endAdornment: !isReadOnly && !generateElementCode && (
											<InputAdornment position="end">
												<Tooltip title={t('GENERATE_CODE_FROM_NAME')}>
													<span>
														<IconButton
															size="small"
															onClick={() => handleGenerateCode(field.onChange)}
															disabled={!namingConfig || isGeneratingCode}
															edge="end"
														>
															<AutorenewIcon
																fontSize="small"
																className={
																	isGeneratingCode ? 'animate-spin' : ''
																}
															/>
														</IconButton>
													</span>
												</Tooltip>
											</InputAdornment>
										),
									}}
								/>
							)}
						/>
					</Tooltip>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<FormControl
						size="small"
						disabled={isReadOnly}
						fullWidth
						sx={[inputFontSizeSx, stereotypeHighlightSx]}
					>
						<InputLabel>{t('STEREOTYPE')}</InputLabel>
						<Controller
							name="stereotypeId"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									value={field.value ?? ''}
									label={t('STEREOTYPE')}
									onChange={(e) => {
										const value = e.target.value ? Number(e.target.value) : null
										field.onChange(value)
										handleFieldChange('stereotypeId', value)
									}}
									renderValue={(value) =>
										getStereotypeLabel(value as number) || <em>-</em>
									}
								>
									{stereotypes?.map((stereotype) => (
										<MenuItem key={stereotype.id} value={stereotype.id}>
											{stereotype.name}
										</MenuItem>
									))}
								</Select>
							)}
						/>
					</FormControl>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<ObjectSettingsMultiselectV2
						onChange={handleObjectSettingsChange}
						disabled={isReadOnly}
						data={form.objectSettings}
						type={ObjectSettingsUpdateDto.StructureTypeEnum.VIEW}
						highlightSx={objectSettingsHighlightSx}
					/>
					{/* Backup: <ObjectSettingsControlV2 ... /> with Objects label above */}
				</Grid2>
			</Grid2>

			{/* Description + Comment - same look as script fields, fullscreen + resize */}
			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<DescriptionFieldWithFullscreen
						title={t('VIEW_DESCRIPTION')}
						value={form.description ?? ''}
						onChange={(v) => handleFieldChange('description', v)}
						readOnly={isReadOnly}
						helperText={t('MARKDOWN_SUPPORTED')}
						containerSx={descriptionHighlightSx}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<DescriptionFieldWithFullscreen
						title={t('VIEW_COMMENT')}
						value={form.comment ?? ''}
						onChange={(v) => handleFieldChange('comment', v)}
						readOnly={isReadOnly}
						helperText={t('MARKDOWN_SUPPORTED')}
						containerSx={commentHighlightSx}
					/>
				</Grid2>
			</Grid2>

			{/* Project Metadata */}
			<TightSectionHeader title={t('PROJECT_METADATA')} />
			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
					<Controller
						name="status"
						control={control}
						render={({ field }) => (
							<Autocomplete
								options={viewStates}
								getOptionLabel={(option) => option.label}
								value={
									field.value
										? viewStates.find((s) => s.value === field.value) ?? null
										: null
								}
								onChange={(_, newValue) => {
									const value = (newValue?.value ?? '') as Status | ''
									field.onChange(value)
									handleFieldChange('status', value)
								}}
								disabled={isReadOnly}
								size="small"
								sx={[
									{
										'& .MuiAutocomplete-clearIndicator': {
											visibility: 'visible',
										},
									},
									statusHighlightSx,
								]}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('VIEW_STATUS')}
										InputLabelProps={{
											...params.InputLabelProps,
											shrink: true,
										}}
										InputProps={{ ...params.InputProps, sx: inputFontSizeSx }}
									/>
								)}
							/>
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
					<Controller
						name="responsiblePersonId"
						control={control}
						render={({ field }) => (
							<Autocomplete
								options={users ?? []}
								getOptionLabel={(option) => option.value ?? ''}
								value={getSelectedUser(field.value)}
								onChange={(_, newValue) => {
									const value = newValue?.id ?? null
									field.onChange(value)
									handleFieldChange('responsiblePersonId', value)
								}}
								disabled={isReadOnly}
								loading={isUserLoading}
								size="small"
								sx={responsiblePersonHighlightSx}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('RESPONSIBLE_PERSON')}
										placeholder={t('SELECT_USER')}
										InputLabelProps={{
											...params.InputLabelProps,
											shrink: true,
										}}
										InputProps={{ ...params.InputProps, sx: inputFontSizeSx }}
									/>
								)}
							/>
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
					<Controller
						name="responsibleAnalystId"
						control={control}
						render={({ field }) => (
							<Autocomplete
								options={users ?? []}
								getOptionLabel={(option) => option.value ?? ''}
								value={getSelectedUser(field.value)}
								onChange={(_, newValue) => {
									const value = newValue?.id ?? null
									field.onChange(value)
									handleFieldChange('responsibleAnalystId', value)
								}}
								disabled={isReadOnly}
								loading={isUserLoading}
								size="small"
								sx={responsibleAnalystHighlightSx}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('RESPONSIBLE_ANALYST')}
										placeholder={t('SELECT_USER')}
										InputLabelProps={{
											...params.InputLabelProps,
											shrink: true,
										}}
										InputProps={{ ...params.InputProps, sx: inputFontSizeSx }}
									/>
								)}
							/>
						)}
					/>
				</Grid2>
			</Grid2>

			{/* Technical Metadata */}
			<TightSectionHeader title={t('TECHNICAL_METADATA')} />
			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
					<Controller
						name="ownerId"
						control={control}
						render={({ field }) => (
							<Autocomplete
								options={systemUsers ?? []}
								getOptionLabel={(option) => option.name ?? ''}
								value={getSelectedSystemUser(field.value)}
								onChange={(_, newValue) => {
									const value = newValue?.id ?? null
									field.onChange(value)
									handleFieldChange('ownerId', value)
								}}
								disabled={isReadOnly}
								loading={isSystemUserLoading}
								size="small"
								sx={ownerHighlightSx}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('OWNER')}
										placeholder={t('SELECT_OWNER')}
										InputLabelProps={{
											...params.InputLabelProps,
											shrink: true,
										}}
										InputProps={{ ...params.InputProps, sx: inputFontSizeSx }}
									/>
								)}
							/>
						)}
					/>
				</Grid2>
			</Grid2>

			{/* Custom Attributes - section header only; no border/label on the fields (hideLabelAndBorder) */}
			{customAttributesFromOverview.length > 0 && (
				<Stack spacing={1.5}>
					<TightSectionHeader title={t('CUSTOM_ATTRIBUTES')} />
					<Form
						defaultValues={overviewInitialValues}
						initialValues={overviewInitialValues}
						disabled={isReadOnly}
						onChange={(values, source) => {
							if (source?.props?.name != null) {
								handleCustomAttributeChange(
									source.props.name as string,
									(values as Record<string, unknown>)[source.props.name as string],
								)
							}
						}}
					>
						<CustomAttributes
							customAttributes={customAttributesFromOverview}
							item={form}
							$pageType={PageType.OVERVIEW_PAGE}
							hideLabelAndBorder
						/>
					</Form>
				</Stack>
			)}
		</Stack>
	)
}
